<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import UIKit, { type ITableColumn } from '@pocketprep/ui-kit'
import { bundleModule } from '@/store/bundle/module'
import { examModule } from '@/store/exam/module'
import { stripeModule } from '@/store/stripe/module'
import { type Stripe as StripeNode } from 'stripe'
import { studentModule } from '@/store/student/module'
import { licenseModule } from '@/store/license/module'
import { organizationModule } from '@/store/organization/module'
import type { Study } from '@pocketprep/types'
import { analyticsModule } from '@/store/analytics/module'
import PurchaseSeatsModalHead from '@/components/SeatsModal/PurchaseSeatsModalHead.vue'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import PaymentFields from '@/components/PaymentFields.vue'
import PurchaseSuccess from '@/components/SeatsModal/PurchaseSuccess.vue'
import { userModule } from '@/store/user/module'
import { instructorModule } from '@/store/instructor/module'
import { groupModule } from '@/store/group/module'
import { useRouter } from 'vue-router'
import { isSupportWindow } from '@/utils'

type TMappedExam = {
    id: string
    examPrep: string
    displayPrice: string
    pricePerSeat: number
    examGuid: string
    numSeats: number
    styles?: {
        [key: string]: string
    }
}

type TNewExam = {
    organizationId: string
    examMetadataId: string
    displayName: string
    orgExamId: string
}

const emit = defineEmits<{
    close: []
}>()

const {
    Icon,
    Link: PocketLink,
    Button: PocketButton,
    ModalContainer,
    Table,
    Select: PocketSelect,
    Input: PocketInput,
} = UIKit

const router = useRouter()

const isLoading = ref(false)
const currentStep = ref(1)
const showExamsDropdown = ref(false)
const mappedExams = ref<TMappedExam[]>([])
const newExams = ref<TNewExam[]>([])
const bulkPurchaseDiscounts = ref<Record<string, number>>({})
const purchaseSeatsModal = ref<HTMLElement | null>(null)
const showSeatDetails = ref(false)
const brandColors = BrandColors
const showPurchaseSuccess = ref(false)
const successTitle = ref<string | null>(null)
const isHoveringSeatsHead = ref(false)
const paymentError = ref<string | null>(null)
const seatCreationError = ref<string | null>(null)
const reMountPaymentFields = ref(Date.now())

const examColumns: ITableColumn[] = [{
    name: 'Exam Prep',
    propName: 'examPrep',
    isSortDisabled: true,
    styles: {
        width: '400px',
    },
}, {
    name: 'Price/Seat',
    propName: 'displayPrice',
    isSortDisabled: true,
}, {
    name: '# of Seats',
    propName: 'numSeats',
    isSortDisabled: true,
    labelStyles: {
        textAlign: 'right',
    },
}]

const discountPricingTableColumns = computed(() => {
    // Only return two columns if the mappedExams are not in the same bundle
    const allCols = [{
        name: '# of Seats',
        propName: 'numSeats',
        isSortDisabled: true,
    }, {
        name: 'Discount',
        propName: 'discount',
        isSortDisabled: true,
        labelStyles: {
            textAlign: 'right',
        },
        styles: {
            textAlign: 'right',
        },
    }, {
        name: 'Price/Seat',
        propName: 'pricePerSeat',
        isSortDisabled: true,
        labelStyles: {
            textAlign: 'right',
        },
        styles: {
            textAlign: 'right',
        },
    }]
    // return without the last column if dealing with more than one bundle
    return bundlesForExams.value.length === 1 ? allCols : allCols.slice(0, -1)
})

const user = computed(() => {
    return userModule.state.user
})

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const allExamMetadata = computed(() => {
    return examModule.getters.getExamMetadata()
})

const totalPurchasedSeatsCount = computed(() => {
    return mappedExams.value.reduce((acc, e) => {
        return acc += e.numSeats
    }, 0)
})

const currentOrgInstructor = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructor()
})

const visibleGroups = computed(() => {
    return groupModule.getters.getCurrentVisibleOrgGroups()
})

const purchaseTotal = computed(() => {
    let numTotalSeats = 0

    // Sort descending
    const discountPricingThresholds = Object.keys(bulkPurchaseDiscounts.value).map(Number)
        .sort((a, b) => b - a)


    const subtotalAmount = mappedExams.value.reduce((acc, e) => {
        // Increment total # of seats to help with discount calculation
        numTotalSeats += e.numSeats
        return acc + (e.numSeats * e.pricePerSeat)
    }, 0)

    // Extract the appropriate bulk discount threshold for the purchase
    const matchedPriceThreshold = discountPricingThresholds.find(dpt => numTotalSeats >= dpt)

    // Giving TS a little help with the key type.. we are fully in control of it so it should always
    // be present on the structure
    const percentageDiscount = matchedPriceThreshold
        ? bulkPurchaseDiscounts.value[matchedPriceThreshold]
        : 0

    const finalTotalAmount = Math.round(percentageDiscount ? subtotalAmount * (1 - percentageDiscount) : subtotalAmount)
    const displayAmount = finalTotalAmount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    })
    const bulkDiscountAmount = Math.round(percentageDiscount ? subtotalAmount * percentageDiscount : 0)
    const bulkDiscountDisplayAmount = bulkDiscountAmount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    })

    return {
        // Multiply by discount if there is one
        finalTotalAmount,
        displayAmount,
        subtotalAmount: subtotalAmount.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
        }),
        bulkDiscountAmount,
        bulkDiscountDisplayAmount,
        percentageDiscount,
        numTotalSeats,
    }
})

// Stripe caps metadata values at 500 characters
const truncateForStripeMetadata = (value: string) => {
    return value.length > 500 ? `${value.slice(0, 497)}...` : value
}

const seatProductMetadata = computed(() => {
    const bundleNames = truncateForStripeMetadata(purchasedBundleNames.value.join(', '))
    const examGuids = truncateForStripeMetadata(purchasedExams.value.map(me => me.examGuid).join(', '))

    return {
        source: 'dashboard_seats',  // manually invoiced seat products are 'seat_invoicing'
        bundle_name: bundleNames, // manually invoiced seat products also use bundle_name
        exam_guids: examGuids,
    }
})

const priceData = computed(() => {
    if (!purchaseTotal.value?.finalTotalAmount) {
        return null
    }
    const prepPriceData: StripeNode.Checkout.SessionCreateParams.LineItem.PriceData = {
        currency: 'usd',
        unit_amount: purchaseTotal.value.finalTotalAmount * 100,
        product_data: {
            name: `${totalPurchasedSeatsCount.value} student seats(${purchasedExams.value
                .map(me => me.examPrep).join(', ')})`,
            metadata: seatProductMetadata.value,
        },
    }
    if (purchaseTotal.value.percentageDiscount && prepPriceData.product_data) {
        prepPriceData.product_data.description = `${purchaseTotal.value.subtotalAmount} - ${
            purchaseTotal.value.bulkDiscountDisplayAmount}(${purchaseTotal.value.percentageDiscount * 100}%)`
    }

    return prepPriceData
})

const paymentIntentMetadata = computed(() => {
    const stringifiedExams = mappedExams.value.map((e) =>
        `${e.examGuid}: ${e.numSeats} @ $${e.pricePerSeat}/seat`).join(', ')
    return {
        subtotal: purchaseTotal.value.subtotalAmount,
        totalSeats: purchaseTotal.value.numTotalSeats,
        paymentPlatform: 'Dashboard',
        exams: truncateForStripeMetadata(stringifiedExams),
        percentageDiscount: purchaseTotal.value.percentageDiscount || null,
    }
})

const purchasedExams = computed(() => {
    return mappedExams.value.filter(me => me.numSeats)
})

const purchasedBundleNames = computed(() => {
    const bundleNamesForPurchasedExams = purchasedExams.value
        .map(purchasedExam => getBundleFromExamGuid(purchasedExam.examGuid)?.name)
        .filter((bundleName): bundleName is string => !!bundleName)
        .sort()
    return [ ...new Set(bundleNamesForPurchasedExams) ]
})

const bundlesForExams = computed(() => {
    return mappedExams.value.reduce((acc, me) => {
        const bundleId = getBundleFromExamGuid(me.examGuid)?.objectId
        if (bundleId && !acc.includes(bundleId)) {
            acc.push(bundleId)
        }
        return acc
    }, [] as string[])
})

const discountPricingTableRows = computed(() => {
    const firstExam = mappedExams.value[0]
    if (!firstExam) {
        return []
    }
    const examGuid = firstExam.examGuid
    const latestMeta = latestExamMetadataForGuid(examGuid)
    if (!latestMeta) {
        return []
    }
    const id = latestMeta.objectId
    const flatPricePerSeat = bundlesForExams.value.length === 1 && getPricePerSeatForExam(examGuid)
    const discountPricing = []
    const discountOptions = Object.keys(bulkPurchaseDiscounts.value)
    for (let i = 0; i < discountOptions.length; i++) {
        const minSeats = Number(discountOptions[i])
        const maxSeats = Number(discountOptions[i + 1]) - 1
        const discountValue = bulkPurchaseDiscounts.value[minSeats] as number
        discountPricing.push({
            id,
            numSeats: maxSeats ? `${minSeats}-${maxSeats}` : `${minSeats}+`,
            discount: `${discountValue * 100}%`,
            ...(flatPricePerSeat) && {
                pricePerSeat: (flatPricePerSeat * (1 - discountValue)).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                }),
            },
        })
    }
    // add the no discount row
    discountPricing.unshift({
        id: 'no-discount',
        numSeats: `1-${Number(discountOptions[0]) - 1}`,
        discount: '0%',
        ...(flatPricePerSeat) && {
            pricePerSeat: `$${flatPricePerSeat}`,
        },
    })

    return discountPricing
})

const receipts = computed(() => {
    return stripeModule.getters.getReceipts()
})

const hasPurchasedInTheLastSixMonths = computed(() => {
    const now = new Date()
    const sixMonthsAgo = new Date()
    sixMonthsAgo.setMonth(now.getMonth() - 6)

    return receipts.value.some(receipt => {
        const receiptCreatedAt = new Date(receipt.created * 1000)
        return receiptCreatedAt >= sixMonthsAgo
    })
})

const allExams = computed(() => {
    // Should return a list of all available exams in this format:
    // { id: exam.objectId value: nativeAppName, label: 'long name' }
    return Object.values(allExamMetadata.value).reduce((acc, em) => {
        const pricePerSeat = getPricePerSeatForExam(em.examGuid)
        const isDiscontinued = examModule.getters.getIsExamMetadataRemoved(em)
        if (mappedExams.value.find(me => me.examGuid === em.examGuid) || !pricePerSeat || isDiscontinued) {
            // Already have the exam listed OR no price OR is discontinued exam
            return acc
        }
        if (!acc.some(e => e.label === em.nativeAppName)) {
            acc.push({
                id: em.objectId,
                label: em.nativeAppName,
                value: em.descriptiveName,
            })
        }
        return acc
    }, [] as { id: string; value: string; label: string }[])
        .sort((a, b) => (a.label.localeCompare(b.label)))
})

const currentOrgLicenses = computed(() => {
    return licenseModule.getters.getCurrentOrgLicenses()
})

const currentOrgStudents = computed(() => {
    return studentModule.getters.getCurrentOrgStudents()
})

const examMetadataGuidLib = computed(() => {
    return examModule.getters.getGuidExamMetadataLib()
})

const getBundleFromExamGuid = (examGuid: string) => {
    const selectedExamMetadata = latestExamMetadataForGuid(examGuid)
    const bundle = selectedExamMetadata && bundleModule.getters.getExamBundle(selectedExamMetadata.objectId)
    return bundle
}

const latestExamMetadataForGuid = (examGuid: string) => {
    return examModule.getters.getSortedExamMetadataForGuid(examGuid)[0]
}

const getPricePerSeatForExam = (examGuid: string) => {
    const bundle = getBundleFromExamGuid(examGuid)
    // Check if override for the exam, otherwise use bundle level pricing
    return bundle?.seatPricingModel?.customExamPrices?.[examGuid]
        || bundle?.seatPricingModel?.costPerSeat
}

const scrollToBottom = () => {
    nextTick(() => {
        if (purchaseSeatsModal.value) {
            purchaseSeatsModal.value.scrollTo({
                top: purchaseSeatsModal.value.scrollHeight,
                behavior: 'smooth',
            })
        }
    })
}

const showExamsList = () => {
    showExamsDropdown.value = true
}

const addNewExam = (newExam: { id: string; value: string; label: string }) => {
    const selectedExamMetadata = allExamMetadata.value[newExam.id]
    const examGuid = selectedExamMetadata?.examGuid
    const pricePerSeat = examGuid && getPricePerSeatForExam(examGuid)
    // Add the new exam to mappedExams[]
    if (pricePerSeat && currentOrganization.value) {
        mappedExams.value.push({
            id: newExam.id,
            examPrep: newExam.label,
            displayPrice: `$${pricePerSeat}.00`,
            pricePerSeat,
            examGuid: selectedExamMetadata?.examGuid || '',
            numSeats: 0,
            styles: { padding: '6px 12px' },
        })

        // Will eventually create new exams/sys groups from these
        newExams.value.push({
            organizationId: currentOrganization.value.objectId,
            examMetadataId: selectedExamMetadata?.objectId || '',
            displayName: selectedExamMetadata?.nativeAppName || '',
            orgExamId: newExam.id,
        })

        analyticsModule.actions.amplitudeTrack('Purchase Seats Exam Selected', {
            sidePanelName: 'Purchase Seats',
            examName: selectedExamMetadata?.nativeAppName,
            organizationId: currentOrganization.value.objectId,
            organizationName: currentOrganization.value.name,
        })
    }
    showExamsDropdown.value = false
}

const goToAddStudents = () => {
    router.push({
        query: {
            addStudents: 'true',
        },
    })
    emit('close')
}

const discountPricingTableClick = () => {
    showSeatDetails.value = !showSeatDetails.value

    analyticsModule.actions.amplitudeTrack('CTA Clicked', {
        ctaLabel: 'View discount pricing table',
        ctaPlacement: 'Purchase Seats sidepanel',
        // Type coercion here is fine since it's really unlikely this'll not be defined
        organizationId: currentOrganization.value?.objectId as string,
        organizationName: currentOrganization.value?.name as string,
    })
}

const submitAddNewExams = () => {
    if (!user.value) {
        throw new Error('submitAddNewExam: Could not find user')
    }
    if (!currentOrganization.value) {
        throw new Error('submitAddNewExam: Could not find current organization')
    }
    if (!currentOrgInstructor.value) {
        throw new Error('submitAddNewExam: Could not find current org instructor')
    }
    newExams.value.filter(newExam => mappedExams.value.find(me => me.id === newExam.orgExamId && me.numSeats))
        .map(async ne => {
        // Add the exam and system group
            const createdOrgExam = await examModule.actions.addOrgExam(ne)
            await groupModule.actions.addGroup({
                organizationId: ne.organizationId,
                name: ne.displayName,
                type: 'system',
                orgExamId: createdOrgExam.objectId,
                orgStudentIds: [],
                isPubliclyEditable: false,
            })

            const newInstructorGroup = await groupModule.actions.addGroup({
                organizationId: ne.organizationId,
                name: `${user.value?.lastName} - ${ne.displayName}`,
                type: 'system',
                orgExamId: createdOrgExam.objectId,
                ownerId: currentOrgInstructor.value?.objectId,
                orgStudentIds: [],
                isPubliclyEditable: false,
            })


            await instructorModule.actions.updateVisibleOrgGroups([
                ...visibleGroups.value
                    .filter(group => group.objectId !== newInstructorGroup.objectId)
                    .map(group => group.objectId),
            ])
        })
}

const purchaseSeats = async (checkoutSessionId: string) => {
    paymentError.value = null

    // extract payment intent ID from stripe checkout session
    const checkoutSession = await stripeModule.actions.fetchCheckoutSession({
        checkoutSessionId: checkoutSessionId,
    })
    const paymentIntentId = checkoutSession.payment_intent as string | null
    const checkoutComplete = checkoutSession.status === 'complete'

    if (!checkoutComplete) {
        paymentError.value = 'Your payment failed or was canceled. Please try again.'
        reMountPaymentFields.value = Date.now()
        return
    }

    const exams = mappedExams.value.reduce((acc, me) => {
        if (!acc[me.examGuid] && me.numSeats) {
            acc[me.examGuid] = me.numSeats
        }
        return acc
    }, {} as { [examGuid: string]: number })

    const finalExamGuids = Object.keys(exams)

    // Extract unique bundles
    const bundles = [ ...new Set(finalExamGuids.map(e => getBundleFromExamGuid(e)?.name)) ]

    // Extract display names
    const displayNames = getDisplayNamesForGuids(finalExamGuids)

    const seatsCount = purchaseTotal.value.numTotalSeats

    // One exam and one bundle
    successTitle.value = bundles.length === 1 && displayNames.length === 1
        ? `${seatsCount} ${displayNames[0]} Seat${seatsCount > 1 ? 's' : ''} Purchased`

        // Multiple exams and one bundle
        : bundles.length === 1 && displayNames.length > 1
            ? `${seatsCount} ${bundles[0]} Seat${seatsCount > 1 ? 's' : ''} Purchased`
            : `${seatsCount} Seat${seatsCount > 1 ? 's' : ''} Purchased`

    showPurchaseSuccess.value = true
    isLoading.value = true

    const studentLicenseLib = currentOrgLicenses.value
        .reduce<{ [studentId: string]: Study.Class.LicenseJSON[] }>((acc, l) => {
            if (!l.orgStudent) {
                return acc
            }
            if (!(l.orgStudent.objectId in acc)) {
                acc[l.orgStudent.objectId] = []
            }
            acc[l.orgStudent.objectId]?.push(l)
            return acc
        }, {})
    const organizationId = currentOrganization.value?.objectId
    const systemExamGroups = Object.values(groupModule.getters.getCurrentOrgGroups())
        .filter(g => g.type === 'system' && !g.owner)
    const studentsMissingLicenses = systemExamGroups.reduce<{ [examGuid: string]: string[] }>((examAcc, og) => {
        const orgExam = currentOrgExams.value[og.orgExam.objectId]
        if (!orgExam) {
            return examAcc
        }
        const examMetadata = allExamMetadata.value[orgExam.examMetadata.objectId]
        if (!examMetadata || !exams[examMetadata.examGuid]) {
            return examAcc
        }

        // iterate over orgStudents adding them if they don't have a license and aren't archived
        const students = og.orgStudents.reduce<Set<string>>((studentAcc, s) => {
            const orgStudent = currentOrgStudents.value[s.objectId]
            const studentLicenses = studentLicenseLib[s.objectId]
            const examLicense = studentLicenses?.find(l => l.examGuid === examMetadata.examGuid)
            if (!orgStudent || orgStudent.isArchived || examLicense) {
                return studentAcc
            }
            studentAcc.add(orgStudent.objectId)
            return studentAcc
        }, new Set())

        if (students.size) {
            examAcc[examMetadata.examGuid] = [ ...students ]
        }

        return examAcc
    }, {})
    if (organizationId && currentOrgInstructor.value && paymentIntentId) {
        // Continue with retrieving seats if the purchase was successful
        try {
            await stripeModule.actions.activateSeats({
                paymentIntentId,
                organizationId,
                exams,
                studentsMissingLicenses,
                orgInstructorId: currentOrgInstructor.value.objectId,
            })


            // Complete the flow of adding exam(s)
            submitAddNewExams()

            // Re-fetch org instructors
            await instructorModule.actions.fetchCurrentOrgInstructors({
                forceFetch: true,
            })

            await Promise.all([
                licenseModule.actions.fetchCurrentOrgLicenses(true),
                stripeModule.actions.fetchReceipts(true),
                stripeModule.actions.fetchPaymentMethods(true),
            ])

            organizationModule.actions.validateOrgState()
        } catch (e) {
            seatCreationError.value = 'There was an error creating licenses within your account. \
            Try refreshing your page or contacting support.'
        }
    }

    isLoading.value = false
}

const getDisplayNamesForGuids = (examGuids: string[]) => {
    return examGuids.map(eg => {
        return examMetadataGuidLib.value[eg]?.[0]?.nativeAppName
    })
}

const cancelUpgrade = () => {
    emit('close')
}

const goBack = () => {
    currentStep.value = currentStep.value - 1
}

const showIntercom = () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('show')
    }
}

const handleDropdown = (isDropdownOpen: boolean) => {
    if (isDropdownOpen) {
        showExamsList()
        scrollToBottom()
    } else {
        showExamsDropdown.value = false
    }
}

onMounted(async () => {
    isLoading.value = true
    await Promise.all([
        bundleModule.actions.fetchBundles(),
        stripeModule.actions.fetchReceipts(true),
        examModule.actions.fetchCurrentOrgExams(),
        stripeModule.actions.fetchPaymentMethods(),
        examModule.actions.fetchExamMetadata(),
        studentModule.actions.fetchCurrentOrgStudents(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])

    bulkPurchaseDiscounts.value = await stripeModule.actions.fetchBulkDiscountPricing()

    // Populate our mappedExams
    mappedExams.value = [ ...Object.values(currentOrgExams.value).reduce<TMappedExam[]>((acc, coe) => {
        const examGuid = allExamMetadata.value[coe.examMetadata.objectId]?.examGuid as string
        const pricePerSeat = getPricePerSeatForExam(examGuid)

        if (!pricePerSeat) {
            // Can't do anything with an exam that has no price
            return acc
        }

        if (!acc.some(e => e.examGuid === examGuid)) {
            acc.push({
                id: coe.objectId,
                examPrep: coe.displayName,
                displayPrice: `$${pricePerSeat}.00`,
                pricePerSeat,
                examGuid,
                // v-model will adjust this value
                numSeats: 0,
                styles: { padding: '6px 12px 5px' },
            })
        }
        return acc
    }, [] as TMappedExam[]) ]

    const seatsDetailsSeenCount = Number(userModule.getters.getCurrentUser()?.webConfig?.hasSeenSeatDetailsCount)

    if (!seatsDetailsSeenCount || seatsDetailsSeenCount < 3 || !hasPurchasedInTheLastSixMonths.value) {
        showSeatDetails.value = true
        const updatedhasSeenSeatDetailsCount = seatsDetailsSeenCount ? seatsDetailsSeenCount + 1 : 1
        await userModule.actions.updateWebConfig({
            hasSeenSeatDetailsCount: updatedhasSeenSeatDetailsCount,
        })
    }

    isLoading.value = false
})

</script>

<template>
    <Portal to="modal">
        <ModalContainer
            class="purchase-seats-modal__container"
            key="upgradeModal"
            :show-close-button="true"
            @close="cancelUpgrade"
        >
            <template #head>
                <PurchaseSeatsModalHead
                    v-if="!showPurchaseSuccess"
                    :current-step="currentStep"
                    @back="goBack"
                />
            </template>
            <div
                class="purchase-seats-modal"
                :class="{
                    'purchase-seats-modal--success': showPurchaseSuccess
                }"
                ref="purchaseSeatsModal"
            >
                <PurchaseSuccess
                    v-if="showPurchaseSuccess"
                    :isLoading="isLoading"
                    :error="seatCreationError"
                    @close="emit('close')"
                    @addStudents="goToAddStudents"
                    @contactSupport="showIntercom"
                >
                    <template #successTitle v-if="successTitle">
                        {{ successTitle }}
                    </template>
                </PurchaseSuccess>
                <template v-else>
                    <template v-if="currentStep === 1">
                        <div
                            class="purchase-seats-modal__add-prep"
                            v-if="!isLoading"
                        >
                            <div class="purchase-seats-modal__main-header">
                                <div class="purchase-seats-modal__main-header-text">
                                    Purchase Seats
                                </div>
                                <div class="purchase-seats-modal__main-header-subtext">
                                    First up is purchasing seats for your organization,
                                    then you'll be able to add students.
                                </div>
                            </div>
                            <div
                                class="purchase-seats-modal__seat-details"
                                :class="{
                                    'purchase-seats-modal__seat-details--expanded': showSeatDetails
                                }"
                            >
                                <div
                                    class="purchase-seats-modal__seat-details-head"
                                    :class="{
                                        'purchase-seats-modal__seat-details-head--expanded': showSeatDetails
                                    }"
                                    @mouseenter="isHoveringSeatsHead = true"
                                    @mouseleave="isHoveringSeatsHead = false"
                                    @click="discountPricingTableClick()"
                                >
                                    <PocketButton
                                        type="tertiary-small"
                                        class="purchase-seats-modal__view-pricing-table-button"
                                        :class="{
                                            'purchase-seats-modal__view-pricing-table-button--focus':
                                                isHoveringSeatsHead
                                        }"
                                    >
                                        📝 Seat Details
                                        <Icon
                                            type="accordionArrow"
                                            :class="{'purchase-seats-modal__accordion--expanded': showSeatDetails}"
                                        />
                                    </PocketButton>
                                </div>
                                <div
                                    v-if="showSeatDetails"
                                    class="purchase-seats-modal__seat-details-content"
                                >
                                    <ul class="purchase-seats-modal__seat-details-bullet-points-table">
                                        <li class="purchase-seats-modal__seat-details-bullet-point">
                                            Each seat is for one student for one exam and never expires.
                                        </li>
                                        <li class="purchase-seats-modal__seat-details-bullet-point">
                                            We offer bulk discounts based on total seats purchased.
                                            Discounts are reflected on the Add Payment step.
                                        </li>
                                    </ul>
                                    <Table
                                        v-if="mappedExams.length"
                                        :rows="discountPricingTableRows"
                                        :columns="discountPricingTableColumns"
                                        :show-header="false"
                                        :key="discountPricingTableColumns.length"
                                        class="purchase-seats-modal__discount-pricing-table"
                                        :table-column-labels-styles="{
                                            paddingLeft: '4px',
                                            paddingRight: '4px',
                                            borderColor: `${brandColors.pewter}D9`
                                        }"
                                        :table-row-styles="{
                                            height: '28px',
                                            padding: '4px 4px 0',
                                            overflow: 'visible',
                                            borderColor: `${brandColors.pewter}D9`
                                        }"
                                        :table-container-styles="{
                                            backgroundColor: brandColors.floralWhite || '',
                                        }"
                                        :table-content-styles="{
                                            border: 'none'
                                        }"
                                        :table-styles="{
                                            width: '237px',
                                        }"
                                        :sort-toggle-order="[ 1, -1 ]"
                                    />
                                    <PocketLink
                                        class="purchase-seats-modal__help-center-link"
                                        href="https://help.educators.pocketprep.com/en/"
                                        target="_blank"
                                    >
                                        <span>Help Center</span>
                                        <Icon type="launch" class="purchase-seats-modal__help-center-icon" />
                                    </PocketLink>
                                </div>
                            </div>
                            <div class="purchase-seats-modal__exam-table-container">
                                <Table
                                    :rows="mappedExams"
                                    :columns="examColumns"
                                    :show-header="false"
                                    class="purchase-seats-modal__table"
                                    :table-column-label-styles="{
                                        paddingLeft: '0px',
                                        fontSize: '12px',
                                        height: '20px',
                                    }"
                                    :table-column-labels-styles="{
                                        backgroundColor: brandColors.pearl || '',
                                        fontSize: '12px',
                                        color: brandColors.brandBlack || '',
                                        height: '28px'
                                    }"
                                    :table-row-styles="{
                                        paddingLeft: '0',
                                        paddingTop: '4px',
                                        paddingBottom: '4px',
                                    }"
                                    :table-cell-styles="{
                                        lineHeight: '36px',
                                        padding: '0',
                                    }"
                                    :sort-toggle-order="[ 1, -1 ]"
                                >
                                    <template #tableCellValue="{ row, column}">
                                        <div
                                            v-if="column.propName === 'numSeats'"
                                        >
                                            <PocketInput
                                                v-model.number="row.numSeats"
                                                :minNumber="0"
                                                fieldType="number"
                                                class="purchase-seats-modal__number-of-seats"
                                            />
                                        </div>
                                    </template>
                                </Table>
                                <div
                                    class="purchase-seats-modal__add-exam-row"
                                    :class="{
                                        'purchase-seats-modal__add-exam-row--active': showExamsDropdown
                                    }"
                                >
                                    <PocketButton
                                        v-if="!showExamsDropdown"
                                        class="purchase-seats-modal__add-exam-button"
                                        type="secondary"
                                        @click="showExamsList"
                                    >
                                        + Add Exam
                                    </PocketButton>
                                    <PocketSelect
                                        v-if="showExamsDropdown"
                                        ref="examDropdown"
                                        id="purchase-seats-modal__exam-search"
                                        class="purchase-seats-modal__exam-search"
                                        placeholder="Search exams"
                                        :auto-focus="true"
                                        :typeahead="true"
                                        :data="allExams"
                                        :dropdown-override="true"
                                        :custom-dropdown-top="48"
                                        @openDropdown="handleDropdown"
                                        @close="showExamsDropdown = false"
                                    >
                                        <template #dropdownListItem="{ item }">
                                            <div
                                                class="purchase-seats-modal__exam-search-li"
                                                @click="addNewExam(item)"
                                            >
                                                <div
                                                    class="purchase-seats-modal__exam-search-li-label"
                                                >
                                                    {{ item.label }}
                                                </div>
                                                <div
                                                    class="purchase-seats-modal__exam-search-li-value"
                                                >
                                                    {{ item.value }}
                                                </div>
                                            </div>
                                        </template>
                                    </PocketSelect>
                                </div>
                            </div>
                            <div class="purchase-seats-modal__add-card-btn-container">
                                <PocketButton
                                    class="purchase-seats-modal__add-card-btn"
                                    :disabled="!totalPurchasedSeatsCount"
                                    @click="currentStep = 2; showExamsDropdown = false"
                                >
                                    Add Card Info <Icon type="arrow" />
                                </PocketButton>
                            </div>
                        </div>
                    </template>
                    <template v-if="currentStep === 2">
                        <div
                            class="purchase-seats-modal__main-header purchase-seats-modal__main-header--wide"
                        >
                            <div class="purchase-seats-modal__main-header-text">
                                Add Payment & Review
                            </div>
                            <div v-if="paymentError" class="purchase-seats-modal__payment-error">
                                {{ paymentError }}
                            </div>
                            <div v-else class="purchase-seats-modal__main-header-subtext">
                                Shipping Address is required solely for tax purposes.
                            </div>
                        </div>
                        <PaymentFields
                            v-if="priceData"
                            mode="payment"
                            :key="`${purchaseTotal.finalTotalAmount}_${reMountPaymentFields}`"
                            :price-data="priceData"
                            :payment-intent-metadata="paymentIntentMetadata"
                            @checkoutCompleted="purchaseSeats"
                        />
                    </template>
                </template>
            </div>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
@use 'sass:color';
.purchase-seats-modal {
    width: 1007px;
    background: $gray-background;
    height: calc(80vh - 56px);
    min-height: 640px;
    box-sizing: border-box;
    overflow: auto;
    border-radius: 0 0 12px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;

    &--success {
        height: 738px;
    }

    &__main-header {
        margin: 36px auto;
        width: 318px;
        text-align: center;

        &--wide {
            width: 450px;
        }
    }

    &__main-header-text {
        font-size: 26px;
        font-weight: 700;
        margin-bottom: 6px;
    }

    &__main-header-subtext {
        color: $ash;
        font-size: 14px;
        line-height: 19px;
    }

    &__seat-details {
        margin: auto;
        width: 600px;
        margin-bottom: 10px;
    }

    &__seat-details-head {
        height: 36px;
        border: 1px solid rgba($pewter, 0.3);
        border-radius: 6px;
        box-sizing: border-box;
        padding: 8px 12px;
        background-color: $floral-white;
        cursor: pointer;

        &--expanded {
            border-radius: 6px 6px 0 0;
        }

        &:hover {
            background-color: $manilla;
        }
    }

    &__seat-details-bullet-points-table {
        margin: 0 0 21px 26px;
        padding: 0 26px 0 18px;
    }

    &__seat-details-content {
        background-color: $floral-white;
        padding-top: 16px;
        padding-bottom: 19px;
        border: 1px solid rgba($pewter, 0.3);
        border-top: none;
        border-radius: 0 0 6px 6px;
    }

    &__accordion {

        &--expanded {
            transform: rotate(180deg);
        }
    }

    &__seat-details-bullet-point {
        font-size: 15px;
        line-height: 20px;
        margin-bottom: 6px;
    }

    &__seat-details-bullet-point:last-child {
        margin-bottom: 0;
    }

    &__discount-pricing-table {
        margin-left: 52px;
        margin-bottom: 27px;
    }

    &__help-center-link {
        display: flex;
        width: 108px;
        margin-left: 29px;
        align-items: center;
    }

    &__help-center-icon {
        height: 14px;
        margin-left: 4px;
    }

    &__exam-table-container {
        width: 600px;

        :deep(.uikit-table__row:last-child) {
            border-bottom: none;
        }
    }

    &__exam-search {
        width: 100%;
        border-radius: 0;

        :deep(.uikit-select__input) {
            height: 48px;
            border-radius: 0 0 3px 3px;

            &::placeholder {
                color: $slate;
                font-size: 16px;
                line-height: 21px;
                font-weight: 500;
            }
        }

        :deep(.uikit-select__input--show-dropdown) {
            border-radius: 0;
            border-width: 2px;
        }
    }

    &__exam-search-li {
        display: flex;
        cursor: pointer;
    }

    &__exam-search-li-label {
        margin-right: 8px;
        white-space: nowrap;
    }

    &__exam-search-li-value {
        color: $ash;
        margin-right: 12px;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    &__add-exam-row {
        background-color: $white;
        border: 1px solid rgba($pewter, 0.3);
        border-top: none;
        border-radius: 0 0 4px 4px;
        display: flex;
        height: 48px;
        padding-left: 6px;
        align-items: center;

        &--active {
            padding-left: 0;
        }

        button {
            height: 36px;
            padding: 6px;
        }
    }

    &__add-card-btn-container {
        margin: 30px 0 200px;
        text-align: center;
    }

    &__view-pricing-table-button {

        &--focus {
            color: color.mix($brand-blue, black, 70%)
        }
    }

    &__payment-error {
        color: $pepper;
        font-size: 14px;
    }

    :deep(.uikit-btn--tertiary-small) {

        &:enabled:hover {
            text-decoration: none;
            color: color.mix($brand-blue, black, 70%);
        }
    }

    :deep(.uikit-input__input) {
        text-align: right;
        padding-right: 4px;
    }

    :deep(table.uikit-table__content) {
        border: 1px solid rgba($pewter, 0.3);
        border-radius: 4px 4px 0 0;
    }

    :deep(.uikit-select__x ) {
        top: 14px;
    }
}
</style>
